// @owners { team: patients-team }
import { useMemo } from 'react';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { getShowingValidationErrors } from '~shared/features/checkout/selectors/getCheckout';
import {
  getOrderAddressId,
  getOrderDeliverMethod,
  getOrderMailRequestConfirmed,
} from '~shared/features/checkout/selectors/getOrder';
import { getOrderIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import getShipmentID from '~shared/features/next-available-date/selectors/getShipmentId';
import { getPrescriptionsByIDs } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { useSelectorShared } from '~shared/store';
import { useQueryMailRequestRequired } from '../queries/queries';

export type CheckoutMailRequestState = {
  mailRequestRequired: boolean;
  mailRequestRequiredMessage: string | null;
  hasMailRequestConfirmedError: boolean;
  showMailRequestConfirmedError: boolean;
};

export type CheckoutMailRequest = CheckoutMailRequestState & {
  addressID: number | null | undefined;
  prescriptionIDs: number[];
  deliveryMethod: string | undefined;
  shipmentID: string | number | null | undefined;
  isOnCompoundCheckoutPage: boolean;
  isLastShipment: boolean;
};

/**
 * Mail-request state for checkout (no analytics). Use {@link useCheckoutTrackMailRequest} when you also need the
 * `CHECKOUT__MAIL_REQUEST_ELIGIBILITY` analytics event; use this hook from {@link useCheckoutValidationErrors} so the
 * screen does not mount two tracking hooks.
 */
export const useCheckoutMailRequest = (): CheckoutMailRequest => {
  const mailRequestConfirmed = useSelectorShared(getOrderMailRequestConfirmed);
  const deliveryMethod = useSelectorShared(getOrderDeliverMethod);
  const orderIndex = useSelectorShared(getOrderIndex);
  const shipmentID = useSelectorShared(getShipmentID);
  const addressID = useSelectorShared(getOrderAddressId);
  const showingValidationErrors = useSelectorShared(getShowingValidationErrors);
  const { cartItemsByDeliveryMethod, deliveryMethods } = useCartDeliveryMethods();
  const isLastShipment = orderIndex === deliveryMethods.length - 1;
  const deliveryMethodForShipment = deliveryMethods[orderIndex];
  const deliveryMethodName = deliveryMethodForShipment?.name;
  const prescriptionItemsForShipment = useMemo(
    () => (deliveryMethodName ? (cartItemsByDeliveryMethod[deliveryMethodName] ?? []) : []),
    [deliveryMethodName, cartItemsByDeliveryMethod],
  );
  const prescriptionIDs = useMemo(
    () => prescriptionItemsForShipment.map((item) => item.resource_id),
    [prescriptionItemsForShipment],
  );
  const prescriptions = useSelectorShared((state) => getPrescriptionsByIDs(state, prescriptionIDs));
  const isOnCompoundCheckoutPage = useMemo(() => {
    return (prescriptions || []).every((prescription) => prescription.is_compound);
  }, [prescriptions]);

  const { mailRequestRequired, mailRequestRequiredMessage } = useQueryMailRequestRequired({
    addressID,
    prescriptionIDs,
    deliveryMethod,
    shipmentID,
    isOnCompoundCheckoutPage,
    isLastShipment,
  });

  const hasMailRequestConfirmedError =
    mailRequestRequired && !mailRequestConfirmed && !isOnCompoundCheckoutPage && isLastShipment;
  const showMailRequestConfirmedError = showingValidationErrors && hasMailRequestConfirmedError;

  return {
    mailRequestRequired,
    mailRequestRequiredMessage,
    hasMailRequestConfirmedError,
    showMailRequestConfirmedError,
    addressID,
    prescriptionIDs,
    deliveryMethod,
    shipmentID,
    isOnCompoundCheckoutPage,
    isLastShipment,
  };
};
